import { useState, useEffect } from "react"
import type FieldProps from "../textFieldProps"
import { InputTypes } from "../textFieldProps"
import { getResultColorClass } from "../utilites/get-color-scoring"

const NumbersField = ({
  model,
  setModel,
  campoId,
  placeholder,
  type,
  required,
  onBlur,
  min,
  max,
  campoIdRespuesta,
  editable,
  title,
  tooltip
}: FieldProps & { editable: boolean }) => {
  const [resumeValue, setResumeValue] = useState<any>(undefined)

  useEffect(() => {
    if (campoIdRespuesta && model.resume && model.resume[campoIdRespuesta] !== undefined) {
      setResumeValue(model.resume[campoIdRespuesta])
    }
  }, [model, campoIdRespuesta])

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (onBlur) onBlur(e)
  }

  const [isEmpty, setIsEmpty] = useState(
    model[campoId] === undefined || model[campoId] === null || model[campoId] === "",
  )

  useEffect(() => {
    setIsEmpty(model[campoId] === undefined || model[campoId] === null || model[campoId] === "")
  }, [model, campoId])

  const renderInput = () => {
    const commonProps = {
      campoId,
      model,
      setModel,
      placeholder,
      type,
      onBlur: handleBlur,
      required,
      min,
      max,
      editable,
    }

    switch (type) {
      case InputTypes.Decimal:
        return <DecimalInput {...commonProps} />
      case InputTypes.Number:
      default:
        return <NumberInput {...commonProps} />
    }
  }

  return (
    <div className="w-full relative">
      {title && (
        <div className="flex mb-3">
          <span className="text-md text-[#4A5568] font-medium">{title}</span>
            {tooltip && (
              <div className="relative group inline-block ml-1 mt-1">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="cursor-pointer" >
                    <path d="M12 16V12M12 8H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <div className="absolute z-50 hidden group-hover:block top-full mt-2 left-32 text-center -translate-x-1/2 px-3 py-2 bg-[#171923] text-white text-sm rounded-lg shadow-lg max-w-[412px] w-max break-words" >
                    {tooltip}
                  </div>
              </div>
            )}
            <div className="flex ml-2.5 items-center">
              {resumeValue !== undefined && (
                  <div className={`${typeof resumeValue === "number" ? getResultColorClass(resumeValue) : "bg-[#EDF2F7] text-[#4A5568]"} w-auto px-1 h-[16px] text-center rounded-[2px] text-xs font-normal`}>
                      {typeof resumeValue === "number" ? resumeValue.toFixed(1) : resumeValue}
                  </div>
              )}
            </div>
        </div>
      )}
      <div className="relative">
        {renderInput()}
        <label
          htmlFor={campoId}
          className={`absolute left-3 transition-all duration-300 cursor-pointer text-[12px] ${
            isEmpty ? "top-[10px] text-[14px] text-gray-400" : "-top-1.5 text-xs text-[#A0AEC0] bg-white px-1"
          }`}
        >
          {placeholder}
        </label>
      </div>
    </div>
  )
}

const NumberInput = ({ model, setModel, campoId, required, onBlur, min, max, editable }: FieldProps & { editable: boolean }) => {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "Escape",
      "Enter",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
    ]
    const isNumber = /[0-9]/.test(e.key)

    if (!allowedKeys.includes(e.key) && !isNumber) {
      e.preventDefault()
    }
  }

  const validateAndCleanInput = (input: string): string => {
    return input.replace(/[^0-9]/g, "")
  }

  return (
    <input
        id={campoId}
        autoComplete="true"
        required={required}
        type="text"
        inputMode="numeric"
        className="w-full h-[40px] font-normal text-[#171923] p-3 rounded-[10px] border  cursor-pointer peer"
        placeholder=""
        value={model[campoId] !== undefined ? model[campoId] : ""}
        onKeyDown={handleKeyDown}
        onChange={(e) => {
            const inputValue = e.target.value
            const cleanedValue = validateAndCleanInput(inputValue)

            if (cleanedValue === "") {
    
            setModel((prevModel: any) => ({
                ...prevModel,
                [campoId]: "",
            }))
            } else {
            const numValue = Number.parseInt(cleanedValue, 10)

            if (!isNaN(numValue)) {
                // Apply only max limit during onChange, min check deferred to onBlur
                const limitedValue = Math.min(
                    max !== undefined ? max : Number.MAX_SAFE_INTEGER,
                    numValue
                );

                setModel((prevModel: any) => ({
                ...prevModel,
                [campoId]: limitedValue.toString(),
                }))
            }
            }
        }}
        onBlur={(e) => {
            const currentValue = model[campoId];
            if (currentValue !== undefined && currentValue !== "" && min !== undefined) {
                const numValue = Number.parseInt(currentValue, 10);
                if (!isNaN(numValue) && numValue < min) {
                    // If value is less than min on blur, set it to min
                    setModel((prevModel: any) => ({
                        ...prevModel,
                        [campoId]: min.toString(),
                    }));
                }
            }
            // Call original onBlur handler if provided
            if (onBlur) {
                onBlur(e);
            }
        }}
        disabled={!editable}
    />
  )
}

const DecimalInput = ({ model, setModel, campoId, required, onBlur, min, max, editable }: FieldProps & { editable: boolean }) => {

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "Escape",
      "Enter",
      ".",
      ",",
      "-",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
    ]

    const isNumber = /[0-9]/.test(e.key)

    if (!allowedKeys.includes(e.key) && !isNumber) {
      e.preventDefault()
    }

    if ((e.key === "." || e.key === ",") && (model[campoId]?.toString().includes(".") || model[campoId]?.toString().includes(","))) {
      e.preventDefault()
    }

    if (e.key === "-") {
      const input = e.currentTarget as HTMLInputElement
      const selectionStart = input.selectionStart || 0

      if (selectionStart !== 0 || model[campoId]?.toString().includes("-")) {
        e.preventDefault()
      }
    }
  }

  const validateAndCleanInput = (input: string): string => {
    let normalizedInput = input.replace(/,/g, ".");

    if (normalizedInput === "-") return normalizedInput;

    const isNegative = input.startsWith("-")
    let cleaned = normalizedInput.replace(/[^0-9.-]/g, "");

    if (isNegative) {
      cleaned = "-" + cleaned.replace(/-/g, "")
    } else {
      cleaned = cleaned.replace(/-/g, "")
    }

    const parts = cleaned.split(".")
    if (parts.length > 2) {
      cleaned = parts[0] + "." + parts.slice(1).join("");
      if (isNegative && !cleaned.startsWith('-')) {
        cleaned = '-' + cleaned;
      }
    }

    return cleaned
  }

  return (
    <input
        id={campoId}
        autoComplete="true"
        required={required}
        type="text"
        inputMode="decimal"
        className="w-full h-[40px] font-normal text-[#171923] p-3 rounded-[10px] border cursor-pointer peer"
        placeholder=""
        value={model[campoId] !== undefined ? model[campoId] : ""}
        onKeyDown={handleKeyDown}
        onChange={(e) => {
            const inputValue = e.target.value
            const cleanedValue = validateAndCleanInput(inputValue)

            if (cleanedValue === "" || cleanedValue === "-" || cleanedValue === "." || cleanedValue === "-.") {
                setModel((prevModel: any) => ({
                    ...prevModel,
                    [campoId]: cleanedValue,
                }))
            } else if (cleanedValue.endsWith('.')) {
                const integerPart = Number.parseFloat(cleanedValue);
                if (!isNaN(integerPart)) {
                }
                setModel((prevModel: any) => ({
                    ...prevModel,
                    [campoId]: cleanedValue,
                }));
            } else {
                const numValue = Number.parseFloat(cleanedValue)

                if (!isNaN(numValue)) {
                    let limitedValue = numValue

                    if (min !== undefined && numValue < min) {
                    limitedValue = min
                    }

                    if (max !== undefined && numValue > max) {
                    limitedValue = max
                    }

                    setModel((prevModel: any) => ({
                    ...prevModel,
                    [campoId]: limitedValue.toString(),
                    }))
                }
            }
        }}
        onBlur={(e) => {
            if (model[campoId] === "." || model[campoId] === "-" || model[campoId] === "-.") {
            setModel((prevModel: any) => ({
                ...prevModel,
                [campoId]: "",
            }))
            }
            if (onBlur) onBlur(e)
        }}
        disabled={!editable}
    />
  )
}

export default NumbersField
